import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * File component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconFile extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M4.40348 2.84245C4.55553 2.6904 4.76176 2.60498 4.97678 2.60498H12.47L19.8345 9.96949V20.5842C19.8345 20.7992 19.7491 21.0055 19.597 21.1575C19.445 21.3096 19.2387 21.395 19.0237 21.395H4.97678C4.76176 21.395 4.55553 21.3096 4.40348 21.1575C4.25144 21.0055 4.16602 20.7992 4.16602 20.5842V3.41575C4.16602 3.20072 4.25144 2.9945 4.40348 2.84245ZM4.97678 1.10498C4.36393 1.10498 3.77618 1.34844 3.34282 1.78179C2.90947 2.21514 2.66602 2.8029 2.66602 3.41575V20.5842C2.66602 21.1971 2.90947 21.7848 3.34282 22.2182C3.77618 22.6515 4.36393 22.895 4.97678 22.895H19.0237C19.6366 22.895 20.2243 22.6515 20.6577 22.2182C21.091 21.7848 21.3345 21.1971 21.3345 20.5842V9.65883C21.3345 9.45992 21.2555 9.26915 21.1148 9.1285L13.311 1.32465C13.1703 1.184 12.9795 1.10498 12.7806 1.10498H4.97678ZM8.09839 7.34814C7.68418 7.34814 7.34839 7.68393 7.34839 8.09814C7.34839 8.51236 7.68418 8.84814 8.09839 8.84814H11.2199C11.6341 8.84814 11.9699 8.51236 11.9699 8.09814C11.9699 7.68393 11.6341 7.34814 11.2199 7.34814H8.09839ZM7.34839 12.7803C7.34839 12.3661 7.68418 12.0303 8.09839 12.0303H15.9022C16.3164 12.0303 16.6522 12.3661 16.6522 12.7803C16.6522 13.1945 16.3164 13.5303 15.9022 13.5303H8.09839C7.68418 13.5303 7.34839 13.1945 7.34839 12.7803ZM8.09839 16.7126C7.68418 16.7126 7.34839 17.0484 7.34839 17.4626C7.34839 17.8769 7.68418 18.2126 8.09839 18.2126H15.9022C16.3164 18.2126 16.6522 17.8769 16.6522 17.4626C16.6522 17.0484 16.3164 16.7126 15.9022 16.7126H8.09839Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-file') === undefined) {
  customElements.define('cat-icon-file', BlocksIconFile);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-file': BlocksIconFile;
  }
}
